import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowRight,
  Wand2,
  Cpu,
  Smartphone,
  RadioTower,
  Bot,
  Sparkles,
  PlugZap,
  Blocks,
  Users,
  Brain,
  Store,
  Target,
  Wrench,
  Home,
  BriefcaseBusiness,
  ArrowLeft,
  ArrowUp,
  ChevronDown,
  ClipboardList,
  Clock,
  Eye,
  Maximize2,
  Mic,
  Monitor,
  MoreHorizontal,
  Plus,
  Redo2,
  Search,
  Settings2,
  ThumbsDown,
  ThumbsUp,
  Undo2,
  Check,
  LayoutGrid,
  GitBranch,
  Layers3,
  Globe2,
  LayoutDashboard,
  Server,
  SmartphoneNfc,
  ChevronRight,
} from "lucide-react";
import { TerraMatrixLogoMark } from "@/components/branding/TerraMatrixBrand";
import "./landing-v3.css";

const LogoGlyph = () => (
  <svg
    viewBox="0 0 32 32"
    fill="none"
    style={{ width: "100%", height: "100%" }}
  >
    <defs>
      <linearGradient id="tm-glyph" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#6DD7EE" />
        <stop offset="100%" stopColor="#1CA9CC" />
      </linearGradient>
    </defs>
    <g fill="url(#tm-glyph)">
      <rect x="3" y="4" width="26" height="6" rx="1.4" />
      <rect x="9" y="11.5" width="14" height="5.5" rx="1.4" />
      <rect x="11" y="18.5" width="10" height="5.5" rx="1.4" />
      <rect x="13" y="25.5" width="6" height="4.5" rx="1.4" />
    </g>
  </svg>
);

interface ProofNode {
  icon: React.ReactNode;
  label: string;
  tone?: "primary" | "muted";
}

interface EcosystemItem {
  title: string;
  meta: string;
  summary: string;
  detail: string;
  bullets: string[];
  preview:
    | "skills"
    | "integrations"
    | "microapps"
    | "agents"
    | "workflows"
    | "blocks"
    | "devices"
    | "publish";
}

/* ─── Connected Systems SVG ─── */
const ConnectedViz = () => (
  <div className="conn-viz">
    <svg
      viewBox="0 0 420 380"
      style={{ width: "100%", height: "auto" }}
      fontFamily="Inter Tight"
    >
      <defs>
        <linearGradient id="cloudg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="var(--tm-accent)" stopOpacity="0.18" />
          <stop offset="1" stopColor="var(--tm-accent)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <g stroke="var(--tm-svg-grid)" strokeWidth="1">
        <path d="M0 32 H420 M0 64 H420 M0 96 H420 M0 128 H420 M0 160 H420 M0 192 H420 M0 224 H420 M0 256 H420 M0 288 H420 M0 320 H420 M0 352 H420" />
        <path d="M32 0 V380 M64 0 V380 M96 0 V380 M128 0 V380 M160 0 V380 M192 0 V380 M224 0 V380 M256 0 V380 M288 0 V380 M320 0 V380 M352 0 V380 M384 0 V380" />
      </g>
      <g>
        <rect
          x="100"
          y="24"
          width="220"
          height="78"
          rx="10"
          fill="var(--tm-surface)"
          stroke="var(--tm-accent)"
          strokeWidth="1.5"
        />
        <rect
          x="100"
          y="24"
          width="220"
          height="78"
          rx="10"
          fill="url(#cloudg)"
        />
        <text
          x="118"
          y="48"
          fontSize="11"
          fontWeight="600"
          fill="var(--tm-fg-muted)"
          letterSpacing="1"
        >
          MICROAPP
        </text>
        <text x="118" y="70" fontSize="16" fontWeight="600" fill="var(--tm-fg)">
          Water leak response
        </text>
        <text x="118" y="88" fontSize="12" fill="var(--tm-fg-muted)">
          agents · workflow · governance
        </text>
        <circle cx="302" cy="44" r="5" fill="var(--tm-success)" />
        <circle cx="302" cy="44" r="8" fill="var(--tm-success)" opacity="0.2" />
      </g>
      <g>
        <rect
          x="130"
          y="160"
          width="160"
          height="58"
          rx="8"
          fill="var(--tm-surface)"
          stroke="var(--tm-border)"
          strokeWidth="1.5"
        />
        <text
          x="148"
          y="182"
          fontSize="11"
          fontWeight="600"
          fill="var(--tm-fg-subtle)"
          letterSpacing="1"
        >
          EDGE RUNTIME
        </text>
        <text
          x="148"
          y="202"
          fontSize="13"
          fontWeight="500"
          fill="var(--tm-fg)"
        >
          Local · on-prem
        </text>
        <circle cx="272" cy="180" r="3" fill="var(--tm-accent-hover)" />
      </g>
      <g fontSize="11">
        <rect
          x="10"
          y="284"
          width="90"
          height="72"
          rx="8"
          fill="var(--tm-surface)"
          stroke="var(--tm-border)"
          strokeWidth="1.5"
        />
        <text
          x="55"
          y="308"
          textAnchor="middle"
          fontWeight="600"
          fill="var(--tm-fg)"
        >
          Home Asst.
        </text>
        <text x="55" y="324" textAnchor="middle" fill="var(--tm-fg-subtle)">
          hub
        </text>
        <text
          x="55"
          y="342"
          textAnchor="middle"
          fill="var(--tm-success)"
          fontSize="10"
        >
          ● online
        </text>
        <rect
          x="110"
          y="284"
          width="90"
          height="72"
          rx="8"
          fill="var(--tm-surface)"
          stroke="var(--tm-border)"
          strokeWidth="1.5"
        />
        <text
          x="155"
          y="308"
          textAnchor="middle"
          fontWeight="600"
          fill="var(--tm-fg)"
        >
          MQTT
        </text>
        <text x="155" y="324" textAnchor="middle" fill="var(--tm-fg-subtle)">
          broker
        </text>
        <text
          x="155"
          y="342"
          textAnchor="middle"
          fill="var(--tm-success)"
          fontSize="10"
        >
          ● 12 topics
        </text>
        <rect
          x="210"
          y="284"
          width="90"
          height="72"
          rx="8"
          fill="var(--tm-surface)"
          stroke="var(--tm-border)"
          strokeWidth="1.5"
        />
        <text
          x="255"
          y="308"
          textAnchor="middle"
          fontWeight="600"
          fill="var(--tm-fg)"
        >
          ESP32
        </text>
        <text x="255" y="324" textAnchor="middle" fill="var(--tm-fg-subtle)">
          sensor
        </text>
        <text
          x="255"
          y="342"
          textAnchor="middle"
          fill="var(--tm-warning)"
          fontSize="10"
        >
          ● damp
        </text>
        <rect
          x="310"
          y="284"
          width="100"
          height="72"
          rx="8"
          fill="var(--tm-surface)"
          stroke="var(--tm-border)"
          strokeWidth="1.5"
        />
        <text
          x="360"
          y="308"
          textAnchor="middle"
          fontWeight="600"
          fill="var(--tm-fg)"
        >
          Mobile
        </text>
        <text x="360" y="324" textAnchor="middle" fill="var(--tm-fg-subtle)">
          field tech
        </text>
        <text
          x="360"
          y="342"
          textAnchor="middle"
          fill="var(--tm-success)"
          fontSize="10"
        >
          ● ack'd
        </text>
      </g>
      <g
        stroke="var(--tm-accent)"
        strokeWidth="1.5"
        fill="none"
        strokeDasharray="3 4"
        opacity="0.55"
      >
        <path d="M210 102 V160" />
      </g>
      <g
        stroke="var(--tm-border)"
        strokeWidth="1.3"
        fill="none"
        strokeDasharray="3 4"
      >
        <path d="M150 218 L55 284" />
        <path d="M185 218 L155 284" />
        <path d="M235 218 L255 284" />
        <path d="M270 218 L360 284" />
      </g>
      <g fontSize="10" fill="var(--tm-accent-hover)" fontWeight="500">
        <text x="218" y="138">
          perceive &amp; act
        </text>
      </g>
      <g fontSize="9" fill="var(--tm-fg-subtle)" fontFamily="JetBrains Mono">
        <text x="76" y="252">
          hass
        </text>
        <text x="162" y="252">
          mqtt
        </text>
        <text x="240" y="252">
          tcp
        </text>
        <text x="320" y="252">
          push
        </text>
      </g>
    </svg>
  </div>
);

/* ─── Main Page Component ─── */
export function LandingPageV3() {
  const [scrolled, setScrolled] = useState(false);
  const [selectedEcosystemItem, setSelectedEcosystemItem] =
    useState<EcosystemItem | null>(null);
  const proofNodes: ProofNode[] = [
    { icon: <Wand2 />, label: "AI-assisted build", tone: "primary" },
    { icon: <Sparkles />, label: "Embedded AI", tone: "primary" },
    { icon: <Brain />, label: "Knowledge & memory" },
    { icon: <RadioTower />, label: "Connected systems" },
    { icon: <PlugZap />, label: "Device integrations" },
    { icon: <Smartphone />, label: "Voice & mobile" },
  ];
  const ecosystemItems: EcosystemItem[] = [
    {
      title: "Skills",
      meta: "plugins",
      summary:
        "Reusable capability packages that extend what microapps can do.",
      detail:
        "Skills let teams add focused capability layers without rebuilding the app itself.",
      bullets: [
        "Package repeated workflows and domain logic",
        "Add operator guidance, automations, or AI behaviors",
        "Reuse across multiple microapps and teams",
      ],
      preview: "skills",
    },
    {
      title: "Integrations",
      meta: "APIs",
      summary:
        "Live connections to the systems your operations already depend on.",
      detail:
        "Integrations keep TerraMatrix microapps connected to upstream and downstream systems instead of turning them into isolated tools.",
      bullets: [
        "Sync with CRMs, ticketing systems, and operations software",
        "Bring external state into workflows and dashboards",
        "Trigger actions back into connected systems",
      ],
      preview: "integrations",
    },
    {
      title: "Microapps",
      meta: "templates",
      summary:
        "Starting points that help teams launch faster and expand over time.",
      detail:
        "Reusable microapp templates help teams start from proven structures instead of empty canvases.",
      bullets: [
        "Pre-built operational patterns",
        "Adapt for specific teams or industries",
        "Grow from focused tools into richer systems",
      ],
      preview: "microapps",
    },
    {
      title: "Agents",
      meta: "runtimes",
      summary:
        "Agent behaviors embedded in the app experience, not bolted on after.",
      detail:
        "Agents can monitor, reason, assist, and act within the context of the workflows they support.",
      bullets: [
        "Run inside real operational surfaces",
        "Use context, memory, and permissions",
        "Trigger actions instead of just generating text",
      ],
      preview: "agents",
    },
    {
      title: "Workflows",
      meta: "flows",
      summary:
        "Structured operational flows that connect tasks, approvals, and updates.",
      detail:
        "Workflows let teams coordinate people, systems, and devices across the work that actually happens day to day.",
      bullets: [
        "Approvals, escalations, and coordination",
        "Links between human and system actions",
        "Operational visibility with less manual follow-up",
      ],
      preview: "workflows",
    },
    {
      title: "Blocks",
      meta: "UI + logic",
      summary:
        "Reusable UI and logic units for assembling connected operational software.",
      detail:
        "Blocks reduce repeated implementation work by giving teams shared pieces they can compose into new microapps.",
      bullets: [
        "Visual surfaces and control primitives",
        "Shared logic and interaction patterns",
        "Faster assembly across different use cases",
      ],
      preview: "blocks",
    },
    {
      title: "Devices",
      meta: "edge + IoT",
      summary:
        "Connections to the sensors, runtimes, and hardware in the real world.",
      detail:
        "Device support lets microapps interact with the physical systems that operations teams actually manage.",
      bullets: [
        "Sensors, controllers, and local runtimes",
        "Live state flowing into microapps",
        "Action moving back out to the field",
      ],
      preview: "devices",
    },
    {
      title: "Publishing",
      meta: "future",
      summary:
        "A future layer for distributing microapps and packaged operational tools.",
      detail:
        "Publishing is the next step in making the ecosystem more shareable across teams, clients, and marketplace surfaces.",
      bullets: [
        "Package and distribute reusable solutions",
        "Support broader ecosystem participation",
        "Extend the reach of proven operational patterns",
      ],
      preview: "publish",
    },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="lv3">
      {/* NAV */}
      <nav className={`lv3-nav${scrolled ? " is-scrolled" : ""}`}>
        <div className="wrap lv3-nav-inner">
          <Link className="lv3-brand" to="/">
            <span className="lv3-brand-mark">
              <TerraMatrixLogoMark className="h-full w-full" />
            </span>
            TerraMatrix
          </Link>
          <div className="lv3-nav-links">
            <a href="#product">Platform</a>
            <a href="#audiences">Use cases</a>
            <a href="#connected">Integrations</a>
            <a href="#marketplace">Ecosystem</a>
            <span className="nav-disabled">Pricing</span>
          </div>
          <div className="lv3-nav-cta">
            <Link to="/login" className="btn btn-ghost">
              Sign in
            </Link>
          </div>
        </div>
      </nav>

      <div className="lv3-theme-toggle" aria-label="Theme controls">
        <ThemeToggle />
      </div>

      {/* HERO */}
      <section className="lv3-hero">
        <div className="lv3-hero-bg" />
        <div className="lv3-hero-glow" />
        <div className="wrap lv3-hero-inner">
          <div className="lv3-hero-copy">
            <h1 className="lv3-hero-title">
              Agentic AI for the <span className="accent">physical world.</span>
            </h1>
            <p className="lv3-hero-sub">
              The microapp platform for real-world operations. Build and run
              AI-powered workflows, service tools, dashboards, and connected
              experiences with embedded agents, modular blocks, and built-in
              integrations.
            </p>
            <div className="lv3-hero-ctas">
              <a href="#product" className="btn btn-primary btn-lg">
                See the platform <ArrowRight size={15} />
              </a>
            </div>
            <p className="lv3-hero-support">
              From first prompt to live operational software. AI-assisted
              creation with workflows, governance, voice, mobile, and
              connections to devices and data.
            </p>
          </div>
        </div>
      </section>

      {/* 03. PRODUCT FRAMING */}
      <section id="product" className="sec">
        <div className="wrap">
          <div className="sec-head">
            <span className="sec-eyebrow">What it is</span>
            <h2>AI-powered software that touches the real world.</h2>
            <p>
              TerraMatrix is the platform for building and running focused
              microapps for scheduling, invoicing, monitoring, alerts,
              connected-device control, and the small operational tools that
              keep a business moving. One platform. Embedded AI. Visual editor.
              Live integrations.
            </p>
          </div>

          <div className="lv3-editor-mobile-shell">
            <div className="lv3-editor-mobile-stage">
              <div className="lv3-editor-wrap">
                {/* Toolbar */}
                <div className="lv3-editor-toolbar">
                  <div className="lv3-tb-left">
                    <button className="lv3-tb-btn">
                      <ArrowLeft size={14} />
                    </button>
                    <span className="lv3-tb-link">Projects</span>
                    <span className="lv3-tb-sep" />
                    <div className="lv3-tb-viewport">
                      <Monitor size={13} />
                      <span>Desktop</span>
                      <span className="lv3-tb-dim">1920×1080</span>
                      <ChevronDown size={11} />
                    </div>
                    <span className="lv3-tb-sep" />
                    <div className="lv3-tb-zoom">
                      <Search size={12} />
                      <span>80%</span>
                      <Maximize2 size={12} />
                    </div>
                    <span className="lv3-tb-sep" />
                    <button className="lv3-tb-btn">
                      <Undo2 size={13} />
                    </button>
                    <button className="lv3-tb-btn">
                      <Redo2 size={13} />
                    </button>
                  </div>
                  <div className="lv3-tb-right">
                    <div className="lv3-tb-save-pill">
                      <Check size={12} className="lv3-tb-save-check" />
                      <span className="lv3-tb-save-label">Saved</span>
                      <Clock size={11} className="lv3-tb-save-clock" />
                      <span className="lv3-tb-save-time">just now</span>
                    </div>
                    <div className="lv3-tb-view-group">
                      <button className="lv3-tb-btn">
                        <Eye size={14} />
                      </button>
                      <span className="lv3-tb-view-div" />
                      <button className="lv3-tb-btn">
                        <MoreHorizontal size={14} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Main body: canvas + AI chat */}
                <div className="lv3-editor-body">
                  {/* Canvas */}
                  <div className="lv3-editor-canvas">
                    {/* Breadcrumb row */}
                    <div className="lv3-editor-breadcrumb">
                      <div className="lv3-crumb-left">
                        <span className="lv3-crumb-item">
                          Northfield Plumbing
                        </span>
                        <ChevronRight size={12} className="lv3-crumb-sep" />
                        <span className="lv3-crumb-item active">
                          <Home size={12} />
                          Dashboard
                        </span>
                      </div>
                      <div className="lv3-crumb-right">
                        <LayoutGrid size={14} />
                        <div className="lv3-crumb-page">
                          <Home size={12} />
                          Dashboard<span className="lv3-crumb-count">3</span>
                          <ChevronDown size={11} />
                        </div>
                      </div>
                    </div>
                    <div className="lv3-canvas-grid" />
                    <div className="lv3-canvas-content">
                      {/* Dashboard header */}
                      <div className="lv3-comp lv3-comp-title">
                        Service Dashboard
                      </div>

                      {/* KPI row */}
                      <div className="lv3-comp-row">
                        <div className="lv3-comp lv3-comp-kpi">
                          <div className="kpi-lbl">Active Jobs</div>
                          <div className="kpi-val">12</div>
                        </div>
                        <div className="lv3-comp lv3-comp-kpi">
                          <div className="kpi-lbl">Techs Available</div>
                          <div className="kpi-val">5</div>
                        </div>
                        <div className="lv3-comp lv3-comp-kpi selected">
                          <div className="kpi-lbl">Avg Response</div>
                          <div className="kpi-val">
                            42<small>min</small>
                          </div>
                        </div>
                      </div>

                      {/* Chart */}
                      <div className="lv3-comp lv3-comp-chart">
                        <div className="lv3-chart-head">
                          <span>Response Time</span>
                          <span className="lv3-chart-range">Last 7 days</span>
                        </div>
                        <svg
                          viewBox="0 0 420 100"
                          className="lv3-chart-svg"
                          preserveAspectRatio="none"
                        >
                          <defs>
                            <linearGradient
                              id="lv3cg"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="0%"
                                stopColor="var(--tm-accent)"
                                stopOpacity="0.25"
                              />
                              <stop
                                offset="100%"
                                stopColor="var(--tm-accent)"
                                stopOpacity="0.02"
                              />
                            </linearGradient>
                          </defs>
                          <path
                            d="M0,70 C40,65 70,45 110,38 C150,30 180,50 220,42 C260,34 290,18 340,24 C370,28 400,16 420,10 L420,100 L0,100Z"
                            fill="url(#lv3cg)"
                          />
                          <path
                            d="M0,70 C40,65 70,45 110,38 C150,30 180,50 220,42 C260,34 290,18 340,24 C370,28 400,16 420,10"
                            fill="none"
                            stroke="var(--tm-accent)"
                            strokeWidth="2"
                          />
                        </svg>
                      </div>

                      {/* Table */}
                      <div className="lv3-comp lv3-comp-table">
                        <div className="lv3-tbl-row lv3-tbl-header">
                          <span>Technician</span>
                          <span>Status</span>
                          <span>Location</span>
                          <span>ETA</span>
                        </div>
                        <div className="lv3-tbl-row">
                          <span>M. Torres</span>
                          <span className="st-active">En route</span>
                          <span>1420 Oak St</span>
                          <span>12 min</span>
                        </div>
                        <div className="lv3-tbl-row">
                          <span>K. Singh</span>
                          <span className="st-idle">Available</span>
                          <span>—</span>
                          <span>—</span>
                        </div>
                        <div className="lv3-tbl-row">
                          <span>J. Park</span>
                          <span className="st-active">On site</span>
                          <span>892 Elm Ave</span>
                          <span>—</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* AI Chat Panel */}
                  <aside className="lv3-editor-chat">
                    <div className="lv3-chat-head">
                      <div className="lv3-chat-title">
                        <Sparkles size={14} />
                        AI Assistant
                      </div>
                      <div className="lv3-chat-acts">
                        <Settings2 size={14} />
                        <span>Clear</span>
                      </div>
                    </div>
                    <div className="lv3-chat-msgs">
                      <div className="lv3-msg user">
                        Build me a service dispatch dashboard
                      </div>
                      <div className="lv3-msg ai">
                        I found 3 schemas in your data — <b>jobs</b>,{" "}
                        <b>technicians</b>, and <b>dispatch_log</b>. I'll pull
                        active job counts, tech availability, and response times
                        into KPI cards and a chart.
                        <div className="lv3-msg-fb">
                          <ThumbsUp size={12} />
                          <ThumbsDown size={12} />
                        </div>
                      </div>
                      <div className="lv3-msg user">
                        Set up an agent to watch response times and flag any
                        tech trending above our 45-min SLA
                      </div>
                      <div className="lv3-msg ai">
                        Agent created — it monitors your <b>dispatch_log</b> in
                        real time and tracks each technician's rolling average
                        from <b>jobs</b>. When someone trends past 45 min, it
                        flags them on the dashboard and sends you a summary with
                        suggested schedule changes.
                        <div className="lv3-msg-fb">
                          <ThumbsUp size={12} />
                          <ThumbsDown size={12} />
                        </div>
                      </div>
                    </div>
                    <div className="lv3-chat-input-wrap">
                      <div className="lv3-chat-pill">
                        <div className="lv3-chat-textarea">
                          Describe canvas changes...
                        </div>
                        <div className="lv3-chat-toolbar">
                          <div className="lv3-chat-toolbar-left">
                            <button className="lv3-chat-icon-btn">
                              <Plus size={14} />
                            </button>
                            <button className="lv3-chat-tool-btn">
                              <Wrench size={12} />
                              Tools
                            </button>
                            <button className="lv3-chat-tool-btn">
                              <ClipboardList size={12} />
                              Plan
                              <ChevronDown size={9} />
                            </button>
                          </div>
                          <div className="lv3-chat-toolbar-right">
                            <button className="lv3-chat-icon-btn">
                              <Mic size={14} />
                            </button>
                            <button className="lv3-chat-send">
                              <ArrowUp size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </aside>
                </div>
              </div>
            </div>
          </div>

          <div className="lv3-builder-phases">
            <div className="bph">
              <span className="bpn">1</span>Build
            </div>
            <ChevronRight className="bph-arr" size={14} />
            <div className="bph">
              <span className="bpn">2</span>Edit
            </div>
            <ChevronRight className="bph-arr" size={14} />
            <div className="bph">
              <span className="bpn">3</span>Deploy
            </div>
            <ChevronRight className="bph-arr" size={14} />
            <div className="bph">
              <span className="bpn">4</span>Operate
            </div>
          </div>
        </div>
      </section>

      {/* 03B. MICROAPP EXPLAINER */}
      <section className="sec sec-tight">
        <div className="wrap">
          <div className="sec-head center">
            <span className="sec-eyebrow">What is a microapp?</span>
            <h2>
              Focused software that can start small and grow with the work.
            </h2>
            <p>
              In TerraMatrix, a microapp is a focused piece of operational
              software built to do a specific job well. That does not mean it
              has to stay small. Teams can start with a simple workflow,
              dashboard, intake flow, or control surface, then grow it into a
              richer system with more logic, more data, and more connected
              capabilities over time.
            </p>
          </div>

          <div className="lv3-microapp-build">
            <div className="lv3-microapp-build-copy">
              <span className="sec-eyebrow">Start simple, build upward</span>
              <h3>
                Begin with one useful block. Add more as the workflow grows.
              </h3>
              <p>
                This is the core microapp idea in TerraMatrix. You can launch
                something focused and operational, then layer in more logic,
                more interfaces, and more connected capabilities without
                rebuilding from scratch.
              </p>
            </div>
            <div className="lv3-microapp-build-viz" aria-hidden="true">
              <div className="lv3-build-stage">
                <div className="lv3-build-block block-1" />
                <div className="lv3-build-block block-2" />
                <div className="lv3-build-block block-3" />
                <div className="lv3-build-block block-4" />
              </div>
            </div>
          </div>

          <div className="lv3-microapp-grid">
            <div className="cap lv3-microapp-card">
              <div className="cap-ic">
                <Target size={16} />
              </div>
              <h4>Focused from the start</h4>
              <p>
                A microapp is built around one operational need such as
                dispatch, approvals, monitoring, alerts, customer intake, or
                device control.
              </p>
            </div>
            <div className="cap lv3-microapp-card">
              <div className="cap-ic">
                <Layers3 size={16} />
              </div>
              <h4>Built on shared infrastructure</h4>
              <p>
                Each microapp can use the same AI, integrations, permissions,
                collaboration, data connections, and reusable building blocks
                across the platform.
              </p>
            </div>
            <div className="cap lv3-microapp-card">
              <div className="cap-ic">
                <GitBranch size={16} />
              </div>
              <h4>Designed to expand</h4>
              <p>
                Start with a basic idea and working functionality, then grow it
                into something broader and more complex as the workflow, team,
                and business requirements evolve.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 03C. BUILT INTO EVERY MICROAPP */}
      <section className="lv3-proof lv3-proof-flow">
        <div className="wrap lv3-proof-inner">
          <div className="lv3-proof-copy">
            <span className="lv3-proof-eyebrow">Built into every microapp</span>
            <h2>
              Capabilities that ship with the software, not as add-ons later.
            </h2>
            <p>
              TerraMatrix combines AI-assisted creation, embedded intelligence,
              workflows, knowledge, governance, and real system connections in
              one operational platform. The app you build already knows how to
              work.
            </p>
          </div>
          <div className="lv3-proof-diagram" aria-label="Platform capabilities">
            <svg
              className="lv3-proof-lines"
              viewBox="0 0 720 480"
              aria-hidden="true"
            >
              <g className="lv3-proof-guides">
                <line x1="360" y1="240" x2="355" y2="38" />
                <line x1="360" y1="240" x2="604" y2="128" />
                <line x1="360" y1="240" x2="353" y2="444" />
                <line x1="360" y1="240" x2="114" y2="342" />
                <line x1="360" y1="240" x2="118" y2="122" />
                <line x1="360" y1="240" x2="594" y2="336" />
              </g>
            </svg>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={`out-${i}`}
                className={`lv3-data-dot dot-out dot-${i}`}
              />
            ))}
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={`in-${i}`} className={`lv3-data-dot dot-in dot-${i}`} />
            ))}
            <div className="lv3-proof-core">
              <div className="lv3-proof-core-mark">
                <LogoGlyph />
              </div>
              <div className="lv3-proof-core-copy">
                <span className="lv3-proof-core-label">Microapp</span>
                <strong>Shared platform core</strong>
              </div>
            </div>
            {proofNodes.map((node, index) => {
              const tone =
                node.tone === "primary"
                  ? " is-primary"
                  : node.tone === "muted"
                    ? " is-muted"
                    : "";
              return (
                <div
                  key={node.label}
                  className={`lv3-proof-node node-${index + 1}${tone}`}
                >
                  <span className="pc-ic">{node.icon}</span>
                  {node.label}
                </div>
              );
            })}
          </div>
        </div>
        <div className="wrap lv3-proof-platform">
          <div className="lv3-proof-platform-head">
            <span className="sec-eyebrow">
              The platform behind the microapps
            </span>
            <h3>Reusable blocks you don&apos;t have to build.</h3>
            <p>
              Shared infrastructure for building, running, and improving
              operational software over time without starting from scratch.
            </p>
          </div>
          <div className="cap-grid">
            {[
              {
                icon: <Sparkles size={16} />,
                title: "Build faster with AI",
                desc: "Natural-language generation and a visual editor from prompt to working, editable software.",
              },
              {
                icon: <Cpu size={16} />,
                title: "Embed AI in the experience",
                desc: "Apps include AI-powered assistance, decisions, workflows, and actions inside the experience.",
              },
              {
                icon: <Blocks size={16} />,
                title: "Assemble what you need",
                desc: "Reusable blocks for workflows, integrations, permissions, collaboration, voice, and connected systems.",
              },
              {
                icon: <Brain size={16} />,
                title: "Operate with context",
                desc: "Knowledge, memory, search, and structured context so agents do more than react to a single prompt.",
              },
              {
                icon: <GitBranch size={16} />,
                title: "Collaborate with control",
                desc: "Real-time collaboration, permissions, version history, branches, drafts, and review workflows.",
              },
              {
                icon: <Smartphone size={16} />,
                title: "Extend beyond the desktop",
                desc: "Mobile-native experiences with voice, push, biometric auth, offline sync, and responsive multi-viewport design.",
              },
              {
                icon: <PlugZap size={16} />,
                title: "Connect software to action",
                desc: "Workflows, device integrations, edge runtimes, and physical-world connectivity from insight to action.",
              },
              {
                icon: <Layers3 size={16} />,
                title: "Compose without ceiling",
                desc: "The surface keeps growing as new blocks, skills, and integrations slot into the same microapp.",
              },
            ].map((c) => (
              <div key={c.title} className="cap">
                <div className="cap-ic">{c.icon}</div>
                <h4>{c.title}</h4>
                <p>{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 07. HOW MICROPAPPS OPERATE */}
      <section id="connected" className="sec">
        <div className="wrap">
          <div className="conn-operates">
            <div className="conn-operates-head section-copy-block section-copy-block-md">
              <span className="sec-eyebrow">How a microapp operates</span>
              <h2>
                Software that can perceive, reason, act, and learn in the real
                world.
              </h2>
              <p className="section-copy-lead section-copy-lead-tight">
                TerraMatrix microapps do more than display information. They
                take in signals from users, systems, devices, and events, apply
                context and memory, trigger the right actions, and improve
                through repeated use.
              </p>
            </div>

            <div className="conn-loop-list">
              {[
                {
                  num: "01",
                  title: "Perceive",
                  desc: "Pull signals from users, data, devices, sensors, and operational events.",
                },
                {
                  num: "02",
                  title: "Reason",
                  desc: "Use context, memory, and workflows to understand what actually matters.",
                },
                {
                  num: "03",
                  title: "Act",
                  desc: "Trigger automations, alerts, approvals, device actions, and live updates.",
                },
                {
                  num: "04",
                  title: "Learn",
                  desc: "Improve through reuse, accumulated memory, and team collaboration over time.",
                },
              ].map((step) => (
                <div key={step.num} className="conn-loop-item">
                  <div className="conn-loop-num">{step.num}</div>
                  <div>
                    <h4>{step.title}</h4>
                    <p>{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="conn conn-combined">
              <div>
                <div className="conn-subhead">Connected systems</div>
                <div className="conn-list">
                  {[
                    {
                      icon: <Home size={18} />,
                      title: "Home Assistant",
                      desc: "Smart-home and connected-device integration paths that bring live state into your microapps.",
                    },
                    {
                      icon: <RadioTower size={18} />,
                      title: "MQTT & ESP32",
                      desc: "IoT and edge connectivity through device-aware services and lightweight firmware patterns.",
                    },
                    {
                      icon: <Server size={18} />,
                      title: "Edge runtime",
                      desc: "Local execution and hardware interaction for latency, privacy, and offline tolerance.",
                    },
                    {
                      icon: <SmartphoneNfc size={18} />,
                      title: "Mobile provisioning",
                      desc: "Device-side communication and setup that keeps field techs and homeowners in the loop.",
                    },
                  ].map((item) => (
                    <div key={item.title} className="conn-item">
                      <div className="conn-ic">{item.icon}</div>
                      <div>
                        <h4>{item.title}</h4>
                        <p>{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <ConnectedViz />
            </div>
          </div>
          <p className="conn-pull">
            Not apps on top of data.{" "}
            <b>
              Apps connected to physical systems, operational action, and live
              feedback loops.
            </b>
          </p>
        </div>
      </section>

      {/* 04. AUDIENCE + USE CASES */}
      <section id="audiences" className="sec sec-flow">
        <div className="wrap">
          <div className="aud-head section-copy-block section-copy-block-sm">
            <span className="sec-eyebrow">Built for</span>
            <h2>Operating in the physical world</h2>
            <p className="section-copy-lead section-copy-lead-md">
              Focused microapps for the operational work that keeps real
              businesses running, from field service and connected property to
              approvals, reporting, and day-to-day coordination.
            </p>
          </div>

          <div className="aud-grid">
            <div className="aud-card">
              <div className="aud-card-head">
                <div className="aud-ic">
                  <Wrench size={18} />
                </div>
                <h3>Skilled trades</h3>
              </div>
              <div className="aud-tiles">
                <div className="aud-tile lit">
                  <div className="an">Dispatch</div>
                  <div className="as">4 techs · live</div>
                </div>
                <div className="aud-tile">
                  <div className="an">Invoicing</div>
                  <div className="as">Fri follow-ups</div>
                </div>
                <div className="aud-tile soft">
                  <div className="an">Diagnostics</div>
                  <div className="as">3 flags</div>
                </div>
                <div className="aud-tile">
                  <div className="an">Intake</div>
                  <div className="as">18 today</div>
                </div>
                <div className="aud-tile soft">
                  <div className="an">Quotes</div>
                  <div className="as">2 drafts</div>
                </div>
                <div className="aud-tile">
                  <div className="an">Comms</div>
                  <div className="as">voice + SMS</div>
                </div>
              </div>
              <p className="aud-card-body">
                Back-office and field microapps for scheduling, invoicing,
                diagnostics, coordination, customer comms, and project
                execution.
              </p>
              <div className="aud-more">
                Service work that stays tied to the field{" "}
                <ArrowRight size={12} />
              </div>
            </div>

            <div className="aud-card">
              <div className="aud-card-head">
                <div className="aud-ic">
                  <Home size={18} />
                </div>
                <h3>Smart home &amp; property</h3>
              </div>
              <div className="aud-tiles">
                <div className="aud-tile soft">
                  <div className="an">Water</div>
                  <div className="as">damp · kitchen</div>
                </div>
                <div className="aud-tile">
                  <div className="an">HVAC</div>
                  <div className="as">unit 4B</div>
                </div>
                <div className="aud-tile lit">
                  <div className="an">Cameras</div>
                  <div className="as">2 motion</div>
                </div>
                <div className="aud-tile">
                  <div className="an">Lighting</div>
                  <div className="as">6 scenes</div>
                </div>
                <div className="aud-tile">
                  <div className="an">Security</div>
                  <div className="as">armed</div>
                </div>
                <div className="aud-tile soft">
                  <div className="an">Escalation</div>
                  <div className="as">auto-route</div>
                </div>
              </div>
              <p className="aud-card-body">
                Control and monitoring across water, lighting, HVAC, security,
                and cameras with alerts that escalate into action automatically.
              </p>
              <div className="aud-more">
                Connected property, not disconnected alerts{" "}
                <ArrowRight size={12} />
              </div>
            </div>

            <div className="aud-card">
              <div className="aud-card-head">
                <div className="aud-ic">
                  <BriefcaseBusiness size={18} />
                </div>
                <h3>Small business ops</h3>
              </div>
              <div className="aud-tiles">
                <div className="aud-tile lit">
                  <div className="an">Approvals</div>
                  <div className="as">3 pending</div>
                </div>
                <div className="aud-tile soft">
                  <div className="an">Reports</div>
                  <div className="as">weekly</div>
                </div>
                <div className="aud-tile">
                  <div className="an">Customer</div>
                  <div className="as">NPS 8.7</div>
                </div>
                <div className="aud-tile">
                  <div className="an">Inventory</div>
                  <div className="as">parts · low</div>
                </div>
                <div className="aud-tile soft">
                  <div className="an">Onboarding</div>
                  <div className="as">2 new</div>
                </div>
                <div className="aud-tile">
                  <div className="an">KPIs</div>
                  <div className="as">live board</div>
                </div>
              </div>
              <p className="aud-card-body">
                Service coordination, approvals, reporting, customer workflows,
                and day-to-day visibility without a six-month rollout.
              </p>
              <div className="aud-more">
                Operational software that grows with the business{" "}
                <ArrowRight size={12} />
              </div>
            </div>
          </div>

          <p className="aud-pull">
            Start with <span>real operational pain</span>. Expand through
            repeatable workflows.
          </p>
        </div>
      </section>

      {/* 09. DIFFERENTIATORS */}
      <section className="sec sec-flow">
        <div className="wrap">
          <div className="section-copy-block section-copy-block-md diff-head">
            <span className="sec-eyebrow">Why TerraMatrix</span>
            <h2>
              Most tools generate software. Few are built for real operational
              work.
            </h2>
            <p className="section-copy-lead section-copy-lead-md">
              Built for teams that need software they can edit, connect,
              improve, and run in the real world over time.
            </p>
          </div>
          <div className="diff-grid">
            {[
              {
                icon: <Bot size={14} />,
                title: "Multi-agent architecture",
                desc: "Composed agents, not single-shot generation.",
              },
              {
                icon: <Globe2 size={14} />,
                title: "Physical-world focus",
                desc: "Devices, workflows, and real operations. Not software-only demos.",
              },
              {
                icon: <LayoutDashboard size={14} />,
                title: "Visual editor + ops integrations",
                desc: "Edit and run connected software in one platform.",
              },
              {
                icon: <Sparkles size={14} />,
                title: "Embedded AI in the apps",
                desc: "Not just in the builder. Inside every running experience.",
              },
              {
                icon: <Blocks size={14} />,
                title: "Modular building blocks",
                desc: "Assemble what you need without rebuilding common capabilities.",
              },
              {
                icon: <Brain size={14} />,
                title: "Knowledge & memory",
                desc: "Built into the platform, not bolted onto each app.",
              },
              {
                icon: <Smartphone size={14} />,
                title: "Mobile & edge execution",
                desc: "Runs beyond the browser with voice, offline, and on-device support.",
              },
            ].map((d) => (
              <div key={d.title} className="diff">
                <div className="diff-ic">{d.icon}</div>
                <div>
                  <h4>{d.title}</h4>
                  <p>{d.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 08. MARKETPLACE */}
      <section id="marketplace" className="sec-tight sec-flow">
        <div className="wrap">
          <div className="mkt">
            <div className="section-copy-block section-copy-block-md mkt-copy">
              <span className="sec-eyebrow">
                Ecosystem
                <span className="mkt-tag">Marketplace coming soon</span>
              </span>
              <h2>
                Extend what you build with skills, integrations, reusable
                blocks, and future publishing.
              </h2>
              <p className="section-copy-lead section-copy-lead-md">
                Start focused, then extend with new skills, integrations,
                microapps, and future marketplace participation. The platform is
                built to grow without forcing you to start over.
              </p>
              <p className="mkt-kicker">
                Start focused. Expand as the operation grows.
              </p>
            </div>
            <div>
              <div className="mkt-grid">
                {ecosystemItems.map((item) => (
                  <button
                    key={item.title}
                    type="button"
                    className={`mkt-cell mkt-card${item.title === "Skills" || item.title === "Blocks" ? " feat" : ""}${item.preview === "publish" ? " mkt-cell-dashed" : ""}`}
                    onClick={() => setSelectedEcosystemItem(item)}
                  >
                    <div className={`mkt-preview mkt-preview-${item.preview}`}>
                      <div className="mkt-preview-glow" />
                      <div className="mkt-preview-frame">
                        <div className="mkt-preview-row" />
                        <div className="mkt-preview-row short" />
                        <div className="mkt-preview-chip" />
                      </div>
                    </div>
                    <div className="mkt-card-copy">
                      <div
                        className={`mn${item.preview === "publish" ? " mkt-cell-subtle" : ""}`}
                      >
                        {item.title}
                      </div>
                      <div className="mk">{item.meta}</div>
                    </div>
                  </button>
                ))}
              </div>
              <p className="mkt-meta">
                skills and integrations now · marketplace coming soon
              </p>
            </div>
          </div>
        </div>
      </section>

      <Dialog
        open={!!selectedEcosystemItem}
        onOpenChange={(open) => !open && setSelectedEcosystemItem(null)}
      >
        <DialogContent className="mkt-dialog sm:max-w-[620px] p-0">
          {selectedEcosystemItem && (
            <>
              <DialogHeader className="mkt-dialog-head">
                <div
                  className={`mkt-dialog-preview mkt-preview-${selectedEcosystemItem.preview}`}
                >
                  <div className="mkt-preview-glow" />
                  <div className="mkt-preview-frame">
                    <div className="mkt-preview-row" />
                    <div className="mkt-preview-row short" />
                    <div className="mkt-preview-chip" />
                  </div>
                </div>
                <div className="mkt-dialog-copy">
                  <div className="mkt-dialog-meta">
                    {selectedEcosystemItem.meta}
                  </div>
                  <DialogTitle>{selectedEcosystemItem.title}</DialogTitle>
                  <DialogDescription>
                    {selectedEcosystemItem.summary}
                  </DialogDescription>
                </div>
              </DialogHeader>
              <div className="mkt-dialog-body">
                <p>{selectedEcosystemItem.detail}</p>
                <ul>
                  {selectedEcosystemItem.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* 10. CLOSING CTA */}
      <section className="sec-tight">
        <div className="lv3-cta-wrap">
          <div className="lv3-cta-card">
            <div className="lv3-cta-bg" />
            <div className="lv3-cta-glow" />
            <div className="lv3-cta-inner">
              <span className="lv3-cta-eyebrow">
                <Target size={12} />
                Get started
              </span>
              <h2>
                Build the operational software your business actually needs.
              </h2>
              <p>
                Create and run AI-powered microapps for dispatch, approvals,
                monitoring, scheduling, connected devices, and day-to-day
                workflows — without rebuilding common infrastructure from
                scratch.
              </p>
              <div className="lv3-cta-btns">
                <a href="#product" className="btn btn-primary btn-lg">
                  See the platform
                </a>
                <Link to="/contact" className="btn btn-secondary btn-lg">
                  Talk to sales
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer>
        <div className="wrap">
          <div className="foot-top">
            <div className="foot-brand">
              <Link className="lv3-brand" to="/">
                <span className="lv3-brand-mark">
                  <TerraMatrixLogoMark className="h-full w-full" />
                </span>
                TerraMatrix
              </Link>
              <p>
                The microapp platform for real-world operations. Agentic AI for
                trades, smart homes, and small-business ops.
              </p>
            </div>
            <div className="foot-col">
              <h5>Platform</h5>
              <a href="#product">Overview</a>
              <a href="#product">Builder</a>
              <a href="#product">Agents</a>
              <a href="#product">Blocks</a>
              <a href="#product">Governance</a>
            </div>
            <div className="foot-col">
              <h5>Use cases</h5>
              <a href="#audiences">Skilled trades</a>
              <a href="#audiences">Smart home</a>
              <a href="#audiences">SMB operations</a>
              <a href="#audiences">Connected property</a>
            </div>
            <div className="foot-col">
              <h5>Ecosystem</h5>
              <span className="foot-disabled">Integrations</span>
              <span className="foot-disabled">Skills</span>
              <span className="foot-disabled">Templates</span>
              <span className="foot-disabled">Marketplace</span>
            </div>
            <div className="foot-col">
              <h5>Company</h5>
              <span className="foot-disabled">About</span>
              <span className="foot-disabled">Pricing</span>
              <span className="foot-disabled">Blog</span>
              <span className="foot-disabled">Careers</span>
              <span className="foot-disabled">Contact</span>
            </div>
          </div>
          <div className="foot-bot">
            <span>&copy; 2026 TerraMatrix Inc.</span>
            <span>Privacy · Terms · Security · Status</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
